import React, { FC } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import { fmtDay, fmtInt, fmtPct } from './opsCapacityUtils';
import type { OpsCapacityCaptcha, OpsCapacityDayRow } from './types';
import { Empty, SimpleTable } from './OpsCapacitySection';

const GRID = 'rgba(148, 163, 184, 0.15)';

const CHALLENGE_KIND_LABELS: Record<string, string> = {
  recaptcha: 'reCAPTCHA',
  hcaptcha: 'hCaptcha',
  cloudflare_turnstile: 'Cloudflare Turnstile',
  press_and_hold: 'Press and hold',
  image_puzzle: 'Image puzzle',
  other: 'Other',
};

const labelForKind = (kind: string): string =>
  CHALLENGE_KIND_LABELS[kind] ?? kind;

const series = (
  label: string,
  data: number[],
  rgb: string,
): {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  fill: boolean;
  tension: number;
  pointRadius: number;
  borderWidth: number;
} => ({
  label,
  data,
  borderColor: `rgb(${rgb})`,
  backgroundColor: `rgba(${rgb}, 0.12)`,
  fill: true,
  tension: 0.25,
  pointRadius: 2,
  borderWidth: 2,
});

interface Props {
  captcha: OpsCapacityCaptcha;
  daily: OpsCapacityDayRow[];
  today: string;
}

/**
 * CAPTCHA demand over time: classified historical interventions / web actions
 * (backfilled) next to the newer solver observations, plus the challenge-kind
 * mix and how much of the source data has been classified.
 */
export const CaptchaSection: FC<Props> = ({ captcha, daily, today }) => {
  const { historical, solver } = captcha;
  const { interventions, webActions, coverage } = historical;
  const byChallengeKind = [...historical.byChallengeKind].sort(
    (a, b) => b.count - a.count,
  );
  const kindTotal = byChallengeKind.reduce((sum, row) => sum + row.count, 0);
  const historicalTotal = interventions.total + webActions.total;
  const unclassified =
    coverage.totalInterventions -
    coverage.classifiedInterventions +
    coverage.totalWebActions -
    coverage.classifiedWebActions;

  if (historicalTotal + solver.total + unclassified === 0) {
    return <Empty>No CAPTCHA-related activity in this range.</Empty>;
  }

  const labels = daily.map(
    (d) => `${fmtDay(d.date)}${d.date === today ? ' *' : ''}`,
  );
  const lineOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxTicksLimit: 31, autoSkip: true },
      },
      y: { beginAtZero: true, grid: { color: GRID }, ticks: { precision: 0 } },
    },
  };

  return (
    <>
      <div className='ops-kpis'>
        <div className='ops-kpi ops-kpi--interventions'>
          <span className='ops-kpi-label'>CAPTCHA interventions</span>
          <span className='ops-kpi-value'>{fmtInt(interventions.total)}</span>
          <span className='ops-kpi-sub'>
            {fmtPct(
              coverage.totalInterventions > 0
                ? (interventions.total / coverage.totalInterventions) * 100
                : null,
            )}{' '}
            of {fmtInt(coverage.totalInterventions)} interventions
          </span>
        </div>
        <div className='ops-kpi ops-kpi--web'>
          <span className='ops-kpi-label'>CAPTCHA web actions</span>
          <span className='ops-kpi-value'>{fmtInt(webActions.total)}</span>
          <span className='ops-kpi-sub'>
            {fmtPct(
              coverage.totalWebActions > 0
                ? (webActions.total / coverage.totalWebActions) * 100
                : null,
            )}{' '}
            of {fmtInt(coverage.totalWebActions)} web actions
          </span>
        </div>
        <div className='ops-kpi ops-kpi--captcha-solver'>
          <span className='ops-kpi-label'>Solver events</span>
          <span className='ops-kpi-value'>{fmtInt(solver.total)}</span>
          <span className='ops-kpi-sub'>browser_captcha_solve_events</span>
        </div>
        <div className='ops-kpi'>
          <span className='ops-kpi-label'>Classified</span>
          <span className='ops-kpi-value'>
            {fmtInt(
              coverage.classifiedInterventions + coverage.classifiedWebActions,
            )}
            <span className='ops-kpi-of'>
              {' '}
              / {fmtInt(coverage.totalInterventions + coverage.totalWebActions)}
            </span>
          </span>
          <span className='ops-kpi-sub'>
            {fmtInt(coverage.classifiedInterventions)}/
            {fmtInt(coverage.totalInterventions)} interventions ·{' '}
            {fmtInt(coverage.classifiedWebActions)}/
            {fmtInt(coverage.totalWebActions)} web actions
          </span>
        </div>
      </div>

      {historicalTotal + solver.total === 0 ? (
        <Empty>
          {fmtInt(unclassified)} intervention(s)/web action(s) in this range not
          yet classified — run the backfill or wait for the daily job.
        </Empty>
      ) : (
        <>
          <div className='ops-chart ops-chart--tall'>
            <Line
              options={lineOptions}
              data={{
                labels,
                datasets: [
                  series(
                    'CAPTCHA interventions',
                    interventions.perDay,
                    '245, 158, 11',
                  ),
                  series(
                    'CAPTCHA web actions',
                    webActions.perDay,
                    '16, 185, 129',
                  ),
                  series('Solver events', solver.perDay, '99, 102, 241'),
                ],
              }}
            />
          </div>
          {daily.some((d) => d.date === today) && (
            <p className='ops-chart-note'>* today so far (Europe/London)</p>
          )}

          {byChallengeKind.length > 0 && (
            <div className='ops-grid ops-grid--2'>
              <div className='ops-chart'>
                <Bar
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        beginAtZero: true,
                        grid: { color: GRID },
                        ticks: { precision: 0 },
                      },
                      y: {
                        grid: { display: false },
                        ticks: { font: { size: 10 } },
                      },
                    },
                  }}
                  data={{
                    labels: byChallengeKind.map((row) =>
                      labelForKind(row.kind),
                    ),
                    datasets: [
                      {
                        label: 'Classified CAPTCHAs',
                        data: byChallengeKind.map((row) => row.count),
                        backgroundColor: 'rgb(14, 165, 233)',
                        borderRadius: 2,
                      },
                    ],
                  }}
                />
              </div>
              <SimpleTable
                caption={`Challenge kinds (${fmtInt(kindTotal)})`}
                columns={[
                  { key: 'kind', label: 'Kind' },
                  { key: 'count', label: 'Count', align: 'right' },
                  { key: 'share', label: 'Share', align: 'right' },
                ]}
                rows={byChallengeKind.map((row) => ({
                  kind: labelForKind(row.kind),
                  count: fmtInt(row.count),
                  share: fmtPct(
                    kindTotal > 0 ? (row.count / kindTotal) * 100 : null,
                    1,
                  ),
                }))}
                rowKey={(r) => String(r.kind)}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
